import React, { useEffect } from 'react';
import styles from './personalizeOverlay.module.scss';
import CrossRing from '../icons/crossRing';
import GreenCheckMark from '../icons/greenCheckMark';
import classNames from 'classnames';
import IconArrowRight from '../icons/po_icon_arrow_right';
import DesignWithAdviceIcon from '../icons/designWithAdvice';
import SelfDesignIcon from '../icons/selfDesign';
import TabNavigationHelper from '../helpers/tabNavigationHelper';
import { ILocalization, IDesignerAppshellPayload, IPersonalizeOverlayPayload } from '../types';
import { BasketService } from '../basketService/basketService';

interface IPersonalizeOverlayProps {
    localization: ILocalization;
    logoServiceInfoUrl?: string;
    basketItemGuid?: string;
    poIsVisible: boolean;
    hasCustomerLogo: boolean;

    handleDesignYourselfClick: () => void;
    closeOverlay: () => void;
}

export const PersonalizeOverlay = (props: IPersonalizeOverlayProps) => {
    const localization = props.localization;
    const closeButtonRef = React.useRef<HTMLButtonElement>(null);
    const tabNavigation = TabNavigationHelper.instance;
    const basketService = BasketService.instance;

    useEffect(() => {
        if (props.poIsVisible) {
            tabNavigation.setFocusAtt(closeButtonRef.current);
            window.shell.tabNav.focus(closeButtonRef.current as Element);
        }
    }, [props.poIsVisible]);

    function LinkButtonContainer(props: {
        icon: React.ReactElement, label, description,
        onClick(): void, dataTestId: any
    }): React.ReactElement {
        return (
            <div className={styles.link_button} onClick={props.onClick} data-testid={props.dataTestId}
                data-semantic-id={props.dataTestId}
                ref={(el) => tabNavigation.setFocusAtt(el)}>
                <div className={styles.lbtn_title}>
                    <div className={styles.icon}>{props.icon}</div>
                    <div className={styles.label}>{props.label}</div>
                </div>
                <div className={styles.description} dangerouslySetInnerHTML={{ __html: props.description }} />
                <div className={styles.link_arrow}><IconArrowRight /></div>
            </div>
        );
    }

    function DesignLinkButtonContainer(props: {
        icon: React.ReactElement, label, title, bullet_1, bullet_2, customerLogo, 
        onClick(): void, dataTestId: any, hasCustomerLogo: boolean
    }): React.ReactElement {
        return (
            <div className={classNames(styles.link_button, styles.design_link_button)} onClick={props.onClick} data-testid={props.dataTestId}
                data-semantic-id={props.dataTestId}
                ref={(el) => tabNavigation.setFocusAtt(el)}>
                <div className={styles.lbtn_title}>
                    <div className={styles.icon}>{props.icon}</div>
                    <div className={styles.label}>{props.label}</div>
                </div>
                <div className={styles.description} dangerouslySetInnerHTML={{ __html: props.title }} />
                <div className={styles.description_bullet}>
                    <GreenCheckMark />
                    <div className={styles.bullet} dangerouslySetInnerHTML={{ __html: props.bullet_1 }} />
                </div>
                <div className={styles.description_bullet}>
                    <GreenCheckMark />
                    <div className={styles.bullet} dangerouslySetInnerHTML={{ __html: props.bullet_2 }} />
                </div>
                {
                    props.hasCustomerLogo &&
                    <div className={styles.has_customer_logo} dangerouslySetInnerHTML={{ __html: props.customerLogo }} />
                }
                <div className={styles.link_arrow}><IconArrowRight /></div>
            </div>
        );
    }

    function handleDesignWithConsultingClick() {
        if (props.basketItemGuid) {
            basketService.callSimplePersonalize(props.basketItemGuid);
        } else if (props.logoServiceInfoUrl) {
            window.open(props.logoServiceInfoUrl, '_self');
        } else {
            // log error
        }
    }

    return <div className={classNames(styles.po_container,
        { [styles.show_po]: props.poIsVisible })}>
        <div className={styles.personalize_overlay} ref={(el) => tabNavigation.setModalAtt(el)}>
            <div className={styles.po_header}>
                <div className={styles.h_title}>{localization.personalise}</div>
                <div className={styles.h_cross_icon} onClick={props.closeOverlay} data-testid='pdp-mo-close'
                    data-semantic-id='pdp-mo-close'
                    ref={(el) => tabNavigation.setFocusAtt(el)}>
                    <CrossRing />
                </div>
            </div>
            <div className={styles.po_body}>
                <div className={styles.banner_short_info}>
                    <div className={styles.small_banner} />
                    <p className={styles.short_info}>
                        {localization.personaliseInfo}
                    </p>
                </div>
                <DesignLinkButtonContainer
                    icon={<SelfDesignIcon />}
                    label={localization.designYourself}
                    title={localization.clicksForFavoriteDesign}
                    bullet_1={localization.motiveAndTextDesigns}
                    bullet_2={localization.fromOnePiece}
                    customerLogo={localization.customLogoAvailable}
                    onClick={props.handleDesignYourselfClick}
                    dataTestId='pdp-design_yourself'
                    hasCustomerLogo={props.hasCustomerLogo}
                />

                {(props.logoServiceInfoUrl || props.basketItemGuid) &&
                    <LinkButtonContainer
                        icon={<DesignWithAdviceIcon />}
                        label={localization.withConsulting}
                        description={localization.withConsultingInfo}
                        onClick={() => {
                            handleDesignWithConsultingClick()
                        }}
                        dataTestId='pdp-design_with_consulting_info'
                    />
                }
            </div>
            <div className={styles.po_footer}>
                <button className={styles.close_button} onClick={props.closeOverlay} ref={closeButtonRef}>
                    {localization.cancel}</button>
            </div>
        </div>
    </div>
};

